import React, { useEffect, useId, useMemo, useState } from "react";
import { telephonyService } from "../../services/telephonyService";
import { listAppUsers } from "../../services/userService";
import { baueKollegen, istNummer, sucheKollegen, type Kollege } from "./kollegenSuche";

/**
 * „Weiterleiten" im Anruf-Popup: ein Suchfeld für Kollegen (Name oder KMVS-Kürzel)
 * mit Vorschlagsliste (↑/↓/Enter oder Maus). Die Auswahl setzt die Durchwahl des
 * Kollegen als Ziel; eine reine Ziffern-Eingabe gilt weiterhin direkt als Nummer.
 *
 * Die Kollegen-Liste (Nebenstellen-Zuordnungen + Benutzer, ohne die eigene
 * Durchwahl) wird beim ersten Öffnen geladen und modulweit zwischengespeichert —
 * das nächste Weiterleiten, auch in einem späteren Anruf, braucht keinen Abruf mehr.
 */

const CACHE_TTL_MS = 10 * 60 * 1000;
let _kollegen: { at: number; promise: Promise<Kollege[]> } | null = null;

function ladeKollegen(): Promise<Kollege[]> {
    if (_kollegen && Date.now() - _kollegen.at < CACHE_TTL_MS) return _kollegen.promise;
    const promise = Promise.all([
        telephonyService.listMappings(),
        listAppUsers(),
        telephonyService.myExtension().catch(() => null),
    ]).then(([mappings, users, me]) => baueKollegen(mappings, users, me?.extension));
    _kollegen = { at: Date.now(), promise };
    // Fehlschlag nicht cachen → beim nächsten Öffnen erneut versuchen.
    promise.catch(() => {
        if (_kollegen?.promise === promise) _kollegen = null;
    });
    return promise;
}

interface Props {
    /** Weiterleiten an `ziel` (Durchwahl oder Nummer); `rueckfrage` = erst sprechen. */
    onWeiterleiten: (ziel: string, rueckfrage: boolean) => void;
}

const KollegenWeiterleitung: React.FC<Props> = ({ onWeiterleiten }) => {
    const [kollegen, setKollegen] = useState<Kollege[] | null>(null);
    const [ladeFehler, setLadeFehler] = useState(false);
    const [eingabe, setEingabe] = useState("");
    const [auswahl, setAuswahl] = useState<Kollege | null>(null);
    const [offen, setOffen] = useState(false);
    const [markiert, setMarkiert] = useState(-1);
    const listId = useId();
    const inputId = useId();

    useEffect(() => {
        let aktiv = true;
        ladeKollegen()
            .then((l) => {
                if (aktiv) setKollegen(l);
            })
            .catch(() => {
                if (aktiv) setLadeFehler(true);
            });
        return () => {
            aktiv = false;
        };
    }, []);

    const vorschlaege = useMemo(
        () => (auswahl || !kollegen ? [] : sucheKollegen(kollegen, eingabe)),
        [kollegen, eingabe, auswahl],
    );
    const listeSichtbar = offen && vorschlaege.length > 0;

    const nummer = istNummer(eingabe);
    const ziel = auswahl ? auswahl.extension : nummer ? eingabe.trim() : null;

    const waehle = (k: Kollege) => {
        setAuswahl(k);
        setEingabe(k.name);
        setOffen(false);
        setMarkiert(-1);
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setEingabe(v);
        setAuswahl(null);
        setOffen(true);
        // Bei einer Nummer nichts vormarkieren (Enter soll dann keinen Kollegen
        // wählen); bei Name/Kürzel den besten Treffer.
        setMarkiert(istNummer(v) ? -1 : 0);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!offen) setOffen(true);
            setMarkiert((i) => Math.min(i + 1, vorschlaege.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setMarkiert((i) => Math.max(i - 1, -1));
        } else if (e.key === "Enter") {
            if (listeSichtbar && markiert >= 0 && vorschlaege[markiert]) {
                e.preventDefault();
                waehle(vorschlaege[markiert]);
            }
        } else if (e.key === "Escape") {
            if (listeSichtbar) {
                e.preventDefault();
                e.stopPropagation();
                setOffen(false);
            }
        }
    };

    const senden = (rueckfrage: boolean) => {
        if (!ziel) return;
        onWeiterleiten(ziel, rueckfrage);
    };

    let hinweis: React.ReactNode = null;
    if (auswahl) {
        hinweis = (
            <>
                Ziel: <b>{auswahl.name}</b> · Durchwahl {auswahl.extension}
            </>
        );
    } else if (nummer) {
        hinweis = <>Ziel: Nummer {eingabe.trim()}</>;
    } else if (eingabe.trim()) {
        hinweis =
            kollegen === null && !ladeFehler
                ? "Kollegen werden geladen …"
                : vorschlaege.length === 0
                  ? "Kein Kollege gefunden — bitte Namen, Kürzel oder eine Nummer eingeben."
                  : "Bitte einen Kollegen aus der Liste wählen.";
    } else if (ladeFehler) {
        hinweis = "Kollegenliste nicht verfügbar — Durchwahl bitte direkt eingeben.";
    }

    const aktiveOption = listeSichtbar && markiert >= 0 ? `${listId}-${markiert}` : undefined;

    return (
        <div className="callw-transfer">
            <label className="callw-tlabel" htmlFor={inputId}>
                Weiterleiten an
            </label>
            <input
                id={inputId}
                className="callw-tinput"
                value={eingabe}
                onChange={onChange}
                onKeyDown={onKeyDown}
                onFocus={() => setOffen(true)}
                onBlur={() => setOffen(false)}
                placeholder="Name, Kürzel oder Nummer …"
                autoComplete="off"
                autoFocus
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={listeSichtbar}
                aria-controls={listId}
                aria-activedescendant={aktiveOption}
            />
            {listeSichtbar && (
                <ul className="callw-tlist" id={listId} role="listbox" aria-label="Passende Kollegen">
                    {vorschlaege.map((k, i) => (
                        <li
                            key={k.extension}
                            id={`${listId}-${i}`}
                            role="option"
                            aria-selected={i === markiert}
                            className={`callw-topt${i === markiert ? " on" : ""}`}
                            // mousedown statt click: sonst schließt der blur des
                            // Eingabefelds die Liste, bevor der Klick ankommt.
                            onMouseDown={(e) => {
                                e.preventDefault();
                                waehle(k);
                            }}
                            onMouseEnter={() => setMarkiert(i)}
                        >
                            <span className="callw-topt-name">{k.name}</span>
                            {k.kuerzel && <span className="callw-topt-kz">{k.kuerzel}</span>}
                            <span className="callw-topt-ext">{k.extension}</span>
                        </li>
                    ))}
                </ul>
            )}
            {hinweis && <div className="callw-thint">{hinweis}</div>}
            <div className="callw-transfer-btns">
                <button
                    className="callw-btn"
                    disabled={!ziel}
                    onClick={() => senden(false)}
                    type="button"
                    title="Anruf sofort durchstellen, ohne vorher mit dem Kollegen zu sprechen"
                >
                    Direkt
                </button>
                <button
                    className="callw-btn"
                    disabled={!ziel}
                    onClick={() => senden(true)}
                    type="button"
                    title="Erst selbst mit dem Kollegen sprechen, dann durchstellen"
                >
                    Mit Rückfrage
                </button>
            </div>
        </div>
    );
};

export default KollegenWeiterleitung;
